import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '../auth/AuthProvider';
import { fetchMessages, sendMessage, type ChatMessage } from '../legacy-api/chat';
import { LegacyApiError } from '../legacy-api/client';

/** The legacy app polled at the same rate; the exhibitor app polls too. */
export const POLL_INTERVAL_MS = 5000;

/**
 * Adds new messages to the thread. Polls and a just-sent message can deliver
 * the same message twice, so ids are de-duplicated; ids also order the thread.
 */
export function mergeMessages(current: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  if (incoming.length === 0) {
    return current;
  }

  const byId = new Map(current.map((message) => [message.id, message]));

  for (const message of incoming) {
    byId.set(message.id, message);
  }

  return [...byId.values()].sort((a, b) => a.id - b.id);
}

export interface ChatState {
  messages: ChatMessage[];
  /** True until the first load settles. */
  loading: boolean;
  failed: boolean;
  send: (content: string) => Promise<void>;
}

export function useChat(standId: number): ChatState {
  const { token, signOut } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // Read inside the poll without restarting it on every new message.
  const lastIdRef = useRef<number | undefined>(undefined);

  const apply = useCallback((incoming: ChatMessage[]): void => {
    setMessages((current) => {
      const next = mergeMessages(current, incoming);
      lastIdRef.current = next.at(-1)?.id;

      return next;
    });
  }, []);

  const handleError = useCallback(
    (cause: unknown): void => {
      if (cause instanceof LegacyApiError && cause.unauthorized) {
        signOut();
      } else {
        setFailed(true);
      }
    },
    [signOut],
  );

  useEffect(() => {
    if (token === undefined) {
      return undefined;
    }

    // State starts empty per stand: the chat room is keyed by stand, so moving
    // to another stand remounts it rather than resetting state here.
    let cancelled = false;

    const poll = async (): Promise<void> => {
      try {
        const incoming = await fetchMessages(token, standId, lastIdRef.current);

        if (!cancelled) {
          apply(incoming);
          setFailed(false);
        }
      } catch (cause) {
        if (!cancelled) {
          handleError(cause);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void poll();
    const timer = window.setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    return (): void => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [token, standId, apply, handleError]);

  const send = useCallback(
    async (content: string): Promise<void> => {
      if (token === undefined) {
        return;
      }

      try {
        apply([await sendMessage(token, standId, content)]);
      } catch (cause) {
        handleError(cause);
        throw cause;
      }
    },
    [token, standId, apply, handleError],
  );

  return { messages, loading, failed, send };
}
